import { useCallback, useEffect, useMemo, useState, useTransition, type ReactNode } from "react";
import { Provider } from "react-redux";

import { BlockUi, ErrorDialog, LogoLoading } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import type { NodeDesignsEntity } from "@skilitics/nautilus-core-designer-data";
import { assert, loadAction, revertAction } from "@skilitics/nautilus-core-designer-data";
import type { Permissions } from "@skilitics/nautilus-endpoint-definition";

import { useContentHostId, type BackendApi } from "../backend";
import { setRootNodeParentContextAction, type NautilusDesignerAppConfig } from "../state/appConfig";
import { ResponseDesignerProvider, TriggerDesignsProvider, useUtilityConditionBuildables } from "../triggers";
import { localeEffectBuildableCreate } from "../triggers/effect-builders";
import type { GetManifestByTypeId, GetMigrateFromManifestByTypeId, GetPlaceholderManifest } from "../types";
import type { GetValueSubtypeDefinition } from "../values";
import type { RootNodeSettings } from "./persistence";
import { UnsavedChangesHandler } from "./persistence";
import { isNotFoundError } from "./persistence/errors";
import { useRedeliverUnsentServerActions } from "./persistence/useRedeliverUnsentServerActions";
import { useNautilusStore } from "./useNautilusStore";
import { useNodeDesignsInitializer } from "./useNodeDesignsInitializer";

export type NodeDesignsWithBackendProviderProps = {
  api: BackendApi;
  rootNodeSettings: RootNodeSettings;
  getManifest: GetManifestByTypeId;
  getPlaceholderManifest: GetPlaceholderManifest;
  getMigrateFromManifest: GetMigrateFromManifestByTypeId;
  getValueSubtypeDefinition: GetValueSubtypeDefinition;
  children: Exclude<ReactNode, null | undefined | boolean>;
};

export const NodeDesignsWithBackendProvider = ({
  api,
  getManifest,
  getPlaceholderManifest,
  getMigrateFromManifest,
  getValueSubtypeDefinition,
  rootNodeSettings,
  children,
}: NodeDesignsWithBackendProviderProps) => {
  const [fetching, beginFetching] = useTransition();
  const [loaded, setLoaded] = useState(false);

  const initialAppConfig = useMemo(
    (): NautilusDesignerAppConfig => ({
      getManifest,
      getPlaceholderManifest,
      getMigrateFromManifest,
      getValueSubtypeDefinition,
      rootNodeParentContext: rootNodeSettings.initialParentContext,
      activeLang: "",
      // `null` means we don't know that the permissions are yet, so checked actions
      // should neither allow the action or show an upgrade prompt. I guess this means
      // they should be disabled for now?
      permissions: null,
    }),
    [
      getManifest,
      getMigrateFromManifest,
      getPlaceholderManifest,
      getValueSubtypeDefinition,
      rootNodeSettings.initialParentContext,
    ],
  );

  const hostId = useContentHostId();

  const store = useNautilusStore({ api, hostId, initialAppConfig });
  const initializeNodeDesigns = useNodeDesignsInitializer({
    getManifest,
    getPlaceholderManifest,
    getMigrateFromManifest,
    rootNodeTypeId: rootNodeSettings.typeId,
  });

  const { redeliver, discard } = useRedeliverUnsentServerActions({ api, hostId });
  const [versionMismatch, setVersionMismatch] = useState(false);
  const [loadError, setLoadError] = useState<"module-missing" | "redelivery-failed" | null>(null);
  const formatMessage = useFormatMessage(redeliveryFormatStrings);

  const load = useCallback(async () => {
    try {
      await redeliver();
    } catch {
      // Redelivery writes to revisions, whose failures — 404 included — are about the revision, not
      // the module. Only fetching the module itself can tell us the module is gone.
      setLoadError("redelivery-failed");
      return;
    }

    let entity: NodeDesignsEntity | undefined;
    let permissions: Permissions;
    try {
      [entity, permissions] = await Promise.all([api.getNodeDesigns(), api.getPermissions()]);
    } catch (error) {
      if (!isNotFoundError(error)) throw error;
      setLoadError("module-missing");
      return;
    }

    if (entity && entity.appVersion !== APP_VERSION) {
      setVersionMismatch(true);
      return;
    }
    if (entity) {
      const primaryLanguageCode = entity.languages.find((lang) => lang.isPrimary)?.code;
      assert(primaryLanguageCode, "Primary language code must be defined");
      const activeLang = window.interact.getLang();
      const activeLangCode = entity.languages.find((lang) => lang.code === activeLang)?.code;
      const appConfig: NautilusDesignerAppConfig = {
        ...initialAppConfig,
        activeLang: activeLangCode || primaryLanguageCode,
        permissions,
      };
      store.dispatch(loadAction({ ...entity, appConfig: appConfig as unknown as Record<string, unknown> }));
      initializeNodeDesigns(store);
      setLoaded(true);
    }
  }, [api, initialAppConfig, initializeNodeDesigns, redeliver, store]);

  useEffect(() => {
    beginFetching(load);
  }, [load]);

  useEffect(() => {
    store.dispatch(setRootNodeParentContextAction(rootNodeSettings.parentContext));
  }, [rootNodeSettings.parentContext, store]);

  const getContentLanguages = () => store.getState().contentLanguages;

  const utilityConditionBuildables = useUtilityConditionBuildables(store);

  if (versionMismatch) {
    return (
      <>
        <LogoLoading />
        <ErrorDialog title={formatMessage("versionMismatchTitle")} text={formatMessage("versionMismatchText")} />
      </>
    );
  }

  if (loadError) {
    const moduleMissing = loadError === "module-missing";
    return (
      <>
        <LogoLoading />
        <ErrorDialog
          title={formatMessage(moduleMissing ? "moduleNotFoundTitle" : "redeliveryFailedTitle")}
          text={formatMessage(moduleMissing ? "moduleNotFoundText" : "redeliveryFailedText")}
          buttonsProps={[
            {
              label: formatMessage("discardChanges"),
              onClick: () => {
                discard();
                setLoadError(null);
                beginFetching(load);
              },
              color: "error",
            },
            ...(moduleMissing
              ? []
              : [
                  {
                    label: formatMessage("retry"),
                    onClick: () => {
                      setLoadError(null);
                      beginFetching(load);
                    },
                    color: "primary" as const,
                  },
                ]),
          ]}
        />
      </>
    );
  }

  if (fetching || !loaded) {
    return <LogoLoading />;
  }

  return (
    <UnsavedChangesHandler
      onDiscard={() => {
        store.dispatch(revertAction());
        window.location.reload();
      }}
    >
      <Provider store={store}>
        <TriggerDesignsProvider
          utilityConditionBuildables={utilityConditionBuildables}
          coreEffectBuildables={[localeEffectBuildableCreate(getContentLanguages)]}
        >
          <ResponseDesignerProvider coreEffectBuildables={[localeEffectBuildableCreate(getContentLanguages)]}>
            {!!fetching && <BlockUi />}
            {children}
          </ResponseDesignerProvider>
        </TriggerDesignsProvider>
      </Provider>
    </UnsavedChangesHandler>
  );
};

const redeliveryFormatStrings = {
  versionMismatchTitle: {
    en: "Update Available",
  },
  versionMismatchText: {
    en: "A newer version of the application is available. Please refresh the page to continue.",
  },
  redeliveryFailedTitle: {
    en: "Failed to restore unsaved changes",
  },
  redeliveryFailedText: {
    en: "Previously unsaved changes could not be sent to the server. You can retry or discard the changes and start fresh.",
  },
  moduleNotFoundTitle: {
    en: "Module no longer exists",
  },
  moduleNotFoundText: {
    en: "The module these unsaved changes belong to has been deleted. The changes will be discarded.",
  },
  retry: {
    en: "Retry",
  },
  discardChanges: {
    en: "Discard Changes",
  },
};
